import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';

import { useBackButtonToPrevRoute } from '@/hooks/useBackButtonToPrevRoute.tsx';
import { routes } from '@/navigation/routes.tsx';
import { FileData } from '@/shared/interface/userRegistrationData.ts';
import { Steps } from '@/components/common/Steps/Steps.tsx';
import { FormErrors } from '@/components/ProfileData/FormErrors.tsx';
import { UserPhotos } from '@/components/Registration/UserPhotos.tsx';
import { getUserPhotosValidationSchema } from '@/validationSchemas/Registration/RegistrationPageSchemas.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { helpers } from '@/helpers.ts';
import { PhotoKind } from '@/shared/type/userDataFields.ts';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import { Gender } from '@/enums/apiEnums.ts';

export type UserPhotosFields = {
  photos: Array<FileData>;
  photosOrigin: Array<File>;
};

const RegistrationUserPhotos: FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { aboutMe } = useProfileStore();

  const [savingPhotoStates, setSavingPhotoStates] = useState<boolean[]>([
    false,
    false,
    false
  ]);
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  const initialPhotos: FileData[] = (aboutMe?.profile?.v2photos || []).map(
    (photo) => ({
      file: undefined,
      url: {
        [PhotoKind.KIND_PRIMARY]: helpers.getPrimaryPhoto(photo.variants) ?? '',
        [PhotoKind.KIND_THUMB]: helpers.getThumbPhoto(photo.variants) ?? ''
      },
      hid: photo.hid,
      state: photo.state,
      thumbhash: helpers.getThumbHash(photo.variants)
    })
  );

  const [data, setData] = useState<UserPhotosFields>({
    photos: initialPhotos,
    photosOrigin: []
  });

  const validationSchema = getUserPhotosValidationSchema(data);

  const methods = useForm({
    resolver: validationSchema ? zodResolver(validationSchema) : undefined,
    defaultValues: data
  });

  function updateFields(fields: Partial<UserPhotosFields>) {
    setData((prev) => ({ ...prev, ...fields }));
  }

  const isSaving = savingPhotoStates.some((el) => el) || isVideoUploading;

  useBackButtonToPrevRoute(routes.registrationUserData, true, !isSaving);

  const onSubmit = () => {
    navigate(routes.registrationUserSelfie);
  };

  const isMale = aboutMe?.profile?.gender === Gender.MALE;
  const isFemale = aboutMe?.profile?.gender === Gender.FEMALE;
  const hasVideo = (aboutMe?.profile?.v2videos?.length ?? 0) > 0;
  const btnTitle = isMale
    ? 'button.continue'
    : isFemale && !hasVideo
      ? 'button.continue_without_video'
      : 'button.continue';

  return (
    <FormProvider {...methods}>
      <form style={{ paddingTop: '100px', paddingBottom: '80px' }}>
        <Steps count={3} progress={2} />
        <UserPhotos
          {...data}
          updateFields={updateFields}
          isSaving={savingPhotoStates}
          setIsSaving={setSavingPhotoStates}
          isVideoUploading={isVideoUploading}
          onVideoUploadingChange={setIsVideoUploading}
        />
      </form>
      <FormErrors />
      <MainButton
        onClick={methods.handleSubmit(onSubmit)}
        text={t(btnTitle)}
        loading={isSaving}
        disabled={isSaving}
        fixed
      />
    </FormProvider>
  );
};

export default RegistrationUserPhotos;
